/**
 * The arithmetic and the readings behind `/devices`, as pure functions.
 *
 * No React, no clock, no `Date.now()` — the same rule `web/src/overview/fold.ts`
 * states and for the same reason: an edge reachable only by mounting a component is
 * an edge nobody tests. Every result that can be an absence is a **tagged union**,
 * never a number with a sentinel.
 *
 * ── The two axes, and why nothing here merges them ──────────────────────────
 *
 * A device carries two independent verdicts and `openapi.yaml` is explicit that they
 * can disagree in the same response body:
 *
 *   `status`   — the **lifecycle** fact, nine values, stored in `wp_devices.status`,
 *                moved by an administrator or by the `mark_offline` sweep. It says
 *                what the device is *allowed* to do.
 *   `liveness` — what the **clock** says, three values, computed from
 *                `last_heartbeat_at` on every read and never stored, so a sweep that
 *                has stopped running degrades the answer instead of freezing it. It
 *                lags **pessimistically, never optimistically**.
 *
 * `stale` is deliberately absent from the lifecycle enum: "it is not something a
 * device *is*, it is something a clock says about it, and there is no column for it".
 * `PRODUCT.md` §4's seventh row and this repository's standing constraint — *never
 * collapse distinct states* — both land on the same instruction here: four readings
 * out, not a boolean, and the lifecycle badge rendered beside them rather than mixed
 * into them.
 *
 * `Device.online` is on the wire and is read **nowhere in this module or in the
 * screen**. It is `liveness === "online"` and nothing more, so rendering it is the
 * three-into-two collapse this file exists to prevent; a guard in
 * `web/src/screens/devices.test.tsx` asserts the absence over the whole directory.
 */

import type { Device, DeviceHealth, DeviceLiveness, DeviceStatus } from '../api/types';

/* ══════════════════════════════════════════════ the reading, per device ══ */

/**
 * The four readings, which are three liveness values plus one that is not a liveness
 * value at all.
 *
 * `never_reported` is `liveness: 'offline'` **and** `last_heartbeat_at: null`, and it
 * is separated because the two sentences a screen must write are different: "we have
 * not heard from this machine since 09:12" and "we have never heard from this machine
 * at all". `DeviceHealth.offline_minutes` makes the same distinction on the wire —
 * it is `null` when the device has never reported, because "there is no silence to
 * measure from", and a number counted from enrolment would be an invention.
 */
export const FLEET_READINGS = ['online', 'stale', 'offline', 'never_reported'] as const;

export type FleetReading = (typeof FLEET_READINGS)[number];

/**
 * `(liveness, last_heartbeat_at)` → one of four.
 *
 * The `null` check runs on the `offline` branch only. A device that has never
 * reported cannot be `online` or `stale` — both are defined as having been heard
 * from within a window — so a `stale` reading with a null heartbeat would be a
 * server bug, and this function reports it as `stale` rather than silently
 * reclassifying it: inventing a fourth branch for an impossible input would hide the
 * bug instead of showing it.
 */
export function readingOf(liveness: DeviceLiveness, lastHeartbeatAt: string | null): FleetReading {
  if (liveness === 'offline' && lastHeartbeatAt === null) return 'never_reported';
  return liveness;
}

/**
 * The four counts, over whatever devices the page actually holds.
 *
 * Deliberately **not** over the fleet: `GET /devices` is offset-paged, and a census
 * that summed one page while claiming to describe the whole company would be the
 * partial-total mistake Task 44 spent §4 on. The screen prints `counted` beside
 * `total` and says which of the two each number is.
 */
export interface FleetCensus {
  readonly online: number;
  readonly stale: number;
  readonly offline: number;
  readonly never_reported: number;
  /** Devices this census actually looked at — the page, not the fleet. */
  readonly counted: number;
}

export function census(devices: readonly Device[]): FleetCensus {
  const counts: Record<FleetReading, number> = {
    online: 0,
    stale: 0,
    offline: 0,
    never_reported: 0,
  };
  for (const device of devices) {
    counts[readingOf(device.liveness, device.last_heartbeat_at ?? null)] += 1;
  }
  return { ...counts, counted: devices.length };
}

/* ══════════════════════════════════════ what a pending device is doing ══ */

/**
 * The three things that are simultaneously true of a `pending` device, and the
 * reason this screen refuses to summarise them into one.
 *
 * Task 17, decision 2: *"A `pending` device holds a valid credential immediately, and
 * may authenticate and heartbeat but not submit events."* 03 §8 WP-E3 says "pending
 * chỉ được heartbeat"; SEC-04 puts the event refusal at step 0c with
 * `WORKFORCE_DEVICE_NOT_APPROVED`.
 *
 * The operational consequence is the part a fleet screen exists to show: a pending
 * machine that is powered on reads `online` — truthfully, it is talking to us — while
 * every batch it sends is refused and **accumulates in its local queue**. An
 * administrator looking at a green dot would conclude the rollout is fine. It is not:
 * that device is collecting nothing that has landed, and if it is later revoked
 * before it is approved, that queue is exactly what revoke destroys.
 *
 * So `pendingNotice` is a value the row renders **in addition to** both badges, never
 * instead of either.
 */
export function isAwaitingApproval(status: DeviceStatus): boolean {
  return status === 'pending';
}

/* ══════════════════════════════════════════ what a revoke destroys ══════ */

/**
 * The local queue depth a revoke is about to discard — as three cases, not a number.
 *
 * `DeviceHealth.pending_local_events` is *"the last value the Agent reported
 * (`wp_agent_heartbeats.queue_depth`), not a server measurement"*, and it is `null`
 * when the device has never reported, because — `openapi.yaml`'s own words — *"`0`
 * there would be a claim rather than an absence"*.
 *
 * The confirm panel has to say something true about a queue it is about to destroy,
 * and the three true things are different sentences:
 *
 *   `known`   — the Agent last said N. Print N, and say it is the Agent's claim at
 *               its last heartbeat, not a count we hold.
 *   `unknown` — the health read did not happen, or the device never reported. Say
 *               that the queue is destroyed and its size is not known.
 *   `empty`   — the Agent last said zero. Still not "nothing is lost": whatever it
 *               queued *since* that heartbeat goes too.
 */
export type QueueLoss =
  | { readonly kind: 'known'; readonly events: number }
  | { readonly kind: 'empty' }
  | { readonly kind: 'unknown' };

export function queueLoss(health: DeviceHealth | null): QueueLoss {
  if (health === null) return { kind: 'unknown' };
  const pending = health.pending_local_events;
  // `typeof` rather than `=== null`, and deliberately: the wire type says `number |
  // null`, but a body that omitted the member entirely would arrive as `undefined`
  // and `undefined === 0` is false — so a `=== null` check would file a missing field
  // as `{ kind: 'known', events: undefined }` and print "undefined events" into a
  // revoke confirmation. One predicate covers both absences and neither is a
  // comparison the compiler considers impossible.
  if (typeof pending !== 'number') return { kind: 'unknown' };
  if (pending === 0) return { kind: 'empty' };
  return { kind: 'known', events: pending };
}

/**
 * The gate on the second step of revoke.
 *
 * Two independent inputs, because one is a mis-click away from being satisfied:
 *
 *  - a **reason**, ≥ 3 characters after trimming. Not a client invention:
 *    `DeviceRevokeRequest.reason` is `minLength: 3`, and `wp_devices_revoke_ck` and
 *    `wp_device_cred_revoke_ck` both demand one at the database, so a revocation
 *    always says why. Checked here as well so the operator learns it from the form
 *    rather than from a `400`.
 *  - the device's own **handle** typed back — hostname, or the device id when the
 *    device never reported one. A machine that has never told us its hostname is
 *    exactly the machine somebody is most likely to revoke by accident.
 *
 * Both comparisons trim; the handle comparison is case-insensitive, because a
 * hostname is case-insensitive and refusing `KHO-HN-01` against `kho-hn-01` would
 * teach the operator that the gate is arbitrary — and a gate that fires on correct
 * work is the one that gets removed.
 */
export interface RevokeGate {
  readonly reasonOk: boolean;
  readonly handleOk: boolean;
  readonly ready: boolean;
}

export const REVOKE_REASON_MIN = 3;

export function revokeHandle(device: Pick<Device, 'hostname' | 'device_id'>): string {
  const hostname = device.hostname ?? '';
  return hostname.trim() === '' ? device.device_id : hostname;
}

export function revokeGate(handle: string, reasonDraft: string, handleDraft: string): RevokeGate {
  const reasonOk = reasonDraft.trim().length >= REVOKE_REASON_MIN;
  const handleOk =
    handleDraft.trim().toLowerCase() === handle.trim().toLowerCase() && handle.trim() !== '';
  return { reasonOk, handleOk, ready: reasonOk && handleOk };
}
